import {
  type Address,
  concat,
  type Hash,
  hashMessage,
  hashTypedData,
  type Hex,
  hexToNumber,
  numberToHex,
  parseAbi,
  type PublicClient,
  recoverAddress,
  slice,
  toEventSelector,
  type TransactionReceipt,
  TransactionReceiptNotFoundError
} from 'viem'

/** Safe Transaction Service URL prefixes (`https://api.safe.global/tx-service/<prefix>`) per chain id */
const SAFE_TX_SERVICE_PREFIXES: Partial<Record<number, string>> = {
  1: 'eth',
  10: 'oeth',
  130: 'unichain',
  137: 'pol',
  143: 'monad',
  480: 'wc',
  999: 'hyper',
  8453: 'base',
  42161: 'arb1',
  747474: 'katana'
}

const getSafeTxServiceUrl = (chainId: number) => {
  const prefix = SAFE_TX_SERVICE_PREFIXES[chainId]
  return prefix && `https://api.safe.global/tx-service/${prefix}/api/v1`
}

const safeAbi = parseAbi([
  'function nonce() view returns (uint256)',
  'function getOwners() view returns (address[])'
])

// Safe 1.3.0 logs the safeTxHash in the event data, Safe 1.4.x as its first indexed topic. The
// event selector is the same in both.
const EXECUTION_SUCCESS = toEventSelector('ExecutionSuccess(bytes32,uint256)')
const EXECUTION_FAILURE = toEventSelector('ExecutionFailure(bytes32,uint256)')

const SAFE_TX_TYPES = {
  SafeTx: [
    { name: 'to', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'data', type: 'bytes' },
    { name: 'operation', type: 'uint8' },
    { name: 'safeTxGas', type: 'uint256' },
    { name: 'baseGas', type: 'uint256' },
    { name: 'gasPrice', type: 'uint256' },
    { name: 'gasToken', type: 'address' },
    { name: 'refundReceiver', type: 'address' },
    { name: 'nonce', type: 'uint256' }
  ]
} as const

interface ServiceSafeTx {
  safe: Address
  safeTxHash: Hash
  to: Address
  value: string
  data: Hex | null
  operation: number
  safeTxGas: string | number
  baseGas: string | number
  gasPrice: string
  gasToken: Address
  refundReceiver: Address
  nonce: string | number
  confirmations?: { owner: Address; signature: Hex | null }[]
}

interface ServiceSafeTxStatus {
  safeTxHash: Hash
  isExecuted: boolean
  transactionHash: Hash | null
}

/** A transaction proposed to a Safe, identified by its `safeTxHash`. */
export interface SafeProposal {
  safe: Address
  safeTxHash: Hash
  nonce: bigint
}

export type SafeProposalOutcome =
  | { status: 'executed' | 'failed'; hash: Hash; receipt: TransactionReceipt }
  | { status: 'dropped' }

/** The Safe Transaction Service rate-limited a request (HTTP 429). */
export class SafeTxServiceRateLimitError extends Error {
  constructor(readonly retryAfterMs: number | undefined) {
    super('Safe Transaction Service responded 429')
    this.name = 'SafeTxServiceRateLimitError'
  }
}

const parseRetryAfterMs = (value: string | null, now: number): number | undefined => {
  if (value === null) return undefined
  if (/^\d+$/.test(value)) return Number(value) * 1000
  if (!/[a-z]/i.test(value)) return undefined
  const date = Date.parse(value)
  return Number.isFinite(date) ? Math.max(0, date - now) : undefined
}

// Browsers only see Retry-After if Safe exposes it via CORS (it currently doesn't), so
// retryAfterMs is usually undefined in the browser and callers fall back to their default.
const throwOnRateLimit = (response: Response) => {
  if (response.status === 429) {
    throw new SafeTxServiceRateLimitError(
      parseRetryAfterMs(response.headers.get('Retry-After'), Date.now())
    )
  }
}

const lower = (value: string) => value.toLowerCase()

/** Pages of 100 Safe transactions read when looking for a proposal */
const SAFE_LOOKUP_MAX_PAGES = 5
/** Safe nonces below the current one searched, for a proposal the Safe executed before we looked it up */
const SAFE_RECENT_NONCES = 10n

const getSafeTxHash = (chainId: number, safe: Address, entry: ServiceSafeTx) =>
  hashTypedData({
    domain: { chainId, verifyingContract: safe },
    types: SAFE_TX_TYPES,
    primaryType: 'SafeTx',
    message: {
      to: entry.to,
      value: BigInt(entry.value),
      data: entry.data ?? '0x',
      operation: entry.operation,
      safeTxGas: BigInt(entry.safeTxGas),
      baseGas: BigInt(entry.baseGas),
      gasPrice: BigInt(entry.gasPrice),
      gasToken: entry.gasToken,
      refundReceiver: entry.refundReceiver,
      nonce: BigInt(entry.nonce)
    }
  })

const matchesSafeTransaction = (
  entry: ServiceSafeTx,
  tx: { to: Address; data?: Hex; value?: bigint }
) =>
  entry.operation === 0 &&
  lower(entry.to) === lower(tx.to) &&
  lower(entry.data ?? '0x') === lower(tx.data ?? '0x') &&
  BigInt(entry.value) === (tx.value ?? 0n)

/**
 * Resolves the Safe transaction a wallet proposed on our behalf (e.g. Rabby in front of a Safe)
 * from the owner signature it returned. The wallet posts the proposal to the Safe Transaction
 * Service before answering, so the entry carrying that exact signature is the proposal. The search
 * also covers recent nonces below the current one, executed or not, since a 1-of-1 Safe may
 * execute it before we look it up and the service may not have indexed that execution yet.
 * The entry is trusted only after its `safeTxHash` is recomputed from its fields (Safe >= 1.3.0
 * EIP-712 domain), its call matches the one we sent and the signature recovers to a Safe owner.
 * @returns undefined when the chain has no Transaction Service or no entry checks out.
 */
export async function findSafeProposal(params: {
  client: PublicClient
  chainId: number
  safe: Address
  signature: Hex
  tx: { to: Address; data?: Hex; value?: bigint }
  fetchFn?: typeof fetch
}): Promise<SafeProposal | undefined> {
  const { client, chainId, safe, signature, tx, fetchFn = fetch } = params
  const serviceUrl = getSafeTxServiceUrl(chainId)
  if (!serviceUrl) return undefined

  const [onchainNonce, owners] = await Promise.all([
    client.readContract({ address: safe, abi: safeAbi, functionName: 'nonce' }),
    client.readContract({ address: safe, abi: safeAbi, functionName: 'getOwners' })
  ])

  // r || s: wallets normalize the trailing v byte differently.
  const signatureRS = lower(signature.slice(0, 130))
  const recentNonce = onchainNonce > SAFE_RECENT_NONCES ? onchainNonce - SAFE_RECENT_NONCES : 0n
  let entry: ServiceSafeTx | undefined
  let confirmed: Hex | undefined
  let pageUrl: string | null | undefined =
    `${serviceUrl}/safes/${safe}/multisig-transactions/?nonce__gte=${recentNonce}&limit=100`
  for (let page = 0; pageUrl && !confirmed && page < SAFE_LOOKUP_MAX_PAGES; page++) {
    const response = await fetchFn(pageUrl)
    if (!response.ok) throw new Error(`Safe Transaction Service responded ${response.status}`)
    const { results, next } = (await response.json()) as {
      results: ServiceSafeTx[]
      next?: string | null
    }
    for (const candidate of results) {
      confirmed =
        candidate.confirmations?.find(
          c => c.signature && lower(c.signature.slice(0, 130)) === signatureRS
        )?.signature ?? undefined
      if (confirmed) {
        entry = candidate
        break
      }
    }
    pageUrl = next
  }
  if (!entry || !confirmed) return undefined

  const nonce = BigInt(entry.nonce)
  const safeTxHash = getSafeTxHash(chainId, safe, entry)
  // Recover from the stored confirmation, which the Safe checks at execution. Safe encodes eth_sign
  // owner signatures, over the prefixed safeTxHash, with v + 4.
  const v = hexToNumber(slice(confirmed, 64, 65))
  const signer =
    v > 30
      ? await recoverAddress({
          hash: hashMessage({ raw: safeTxHash }),
          signature: concat([slice(confirmed, 0, 64), numberToHex(v - 4, { size: 1 })])
        })
      : await recoverAddress({ hash: safeTxHash, signature: confirmed })
  const isProposal =
    lower(safeTxHash) === lower(entry.safeTxHash) &&
    matchesSafeTransaction(entry, tx) &&
    owners.some(owner => lower(owner) === lower(signer))

  return isProposal ? { safe, safeTxHash, nonce } : undefined
}

/** Resolves a Safe proposal from the safeTxHash returned instead of a transaction hash. @public — used by vaults-app, which knip ignores */
export async function findSafeProposalByHash(params: {
  chainId: number
  safe: Address
  safeTxHash: Hash
  tx: { to: Address; data?: Hex; value?: bigint }
  fetchFn?: typeof fetch
  retryDelaysMs?: readonly number[]
}): Promise<SafeProposal | undefined> {
  const { chainId, safe, safeTxHash, tx, fetchFn = fetch, retryDelaysMs = [1_000, 2_000] } = params
  const serviceUrl = getSafeTxServiceUrl(chainId)
  if (!serviceUrl) return undefined

  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
    let response: Response | undefined
    try {
      response = await fetchFn(`${serviceUrl}/multisig-transactions/${safeTxHash}/`)
    } catch {
      // A failed request gets the same bounded retry as transient service responses.
    }

    if (!response || response.status === 404 || response.status === 429 || response.status >= 500) {
      const delayMs = retryDelaysMs[attempt]
      if (delayMs === undefined) return undefined
      await new Promise<void>(resolve => setTimeout(resolve, delayMs))
      continue
    }
    if (!response.ok) return undefined

    const record = (await response.json()) as ServiceSafeTx
    if (
      lower(record.safe) !== lower(safe) ||
      lower(getSafeTxHash(chainId, safe, record)) !== lower(safeTxHash) ||
      !matchesSafeTransaction(record, tx)
    ) {
      return undefined
    }
    return { safe, safeTxHash, nonce: BigInt(record.nonce) }
  }

  return undefined
}

/**
 * Reads how the Safe settled a proposal from the ExecutionSuccess or ExecutionFailure event it
 * emitted for that safeTxHash in the given transaction.
 * @returns undefined when the receipt is not available yet or holds no such event.
 */
async function readSafeExecution(
  client: PublicClient,
  proposal: SafeProposal,
  hash: Hash
): Promise<SafeProposalOutcome | undefined> {
  let receipt
  try {
    receipt = await client.getTransactionReceipt({ hash })
  } catch (error) {
    if (error instanceof TransactionReceiptNotFoundError) return undefined
    throw error
  }
  const safeTxHash = lower(proposal.safeTxHash)
  const event = receipt.logs.find(
    log =>
      lower(log.address) === lower(proposal.safe) &&
      (log.topics[0] === EXECUTION_SUCCESS || log.topics[0] === EXECUTION_FAILURE) &&
      (lower(log.topics[1] ?? '') === safeTxHash || lower(log.data.slice(0, 66)) === safeTxHash)
  )
  if (!event) return undefined
  return {
    status: event.topics[0] === EXECUTION_SUCCESS ? 'executed' : 'failed',
    hash: receipt.transactionHash,
    receipt
  }
}

/**
 * One tracking round for a proposal, read from its Transaction Service record. An executed record
 * points to the onchain transaction that executed it; the outcome is taken from the Safe's own
 * execution event in that transaction's receipt, not from the service. A record
 * that is gone was deleted from the queue. A Safe nonce is consumed exactly once, so once the Safe
 * nonce has moved past the proposal and the service lists another executed transaction at that
 * nonce, the proposal was rejected or replaced.
 * @returns undefined while the proposal is still pending, while the execution receipt does not
 * confirm it yet, or while the service has not indexed the transaction that consumed its nonce.
 */
export async function checkSafeProposal(params: {
  client: PublicClient
  chainId: number
  proposal: SafeProposal
  fetchFn?: typeof fetch
  signal?: AbortSignal
}): Promise<SafeProposalOutcome | undefined> {
  const { client, chainId, proposal, fetchFn = fetch, signal } = params
  const serviceUrl = getSafeTxServiceUrl(chainId)
  const response = await fetchFn(`${serviceUrl}/multisig-transactions/${proposal.safeTxHash}/`, {
    signal
  })
  if (response.status === 404) return { status: 'dropped' }
  throwOnRateLimit(response)
  if (!response.ok) throw new Error(`Safe Transaction Service responded ${response.status}`)
  const record = (await response.json()) as ServiceSafeTxStatus
  if (record.isExecuted && record.transactionHash) {
    const execution = await readSafeExecution(client, proposal, record.transactionHash)
    if (execution) return execution
  }

  const nonce = await client.readContract({
    address: proposal.safe,
    abi: safeAbi,
    functionName: 'nonce'
  })
  if (nonce <= proposal.nonce) return undefined

  const atNonce = await fetchFn(
    `${serviceUrl}/safes/${proposal.safe}/multisig-transactions/?executed=true&nonce=${proposal.nonce}`,
    { signal }
  )
  throwOnRateLimit(atNonce)
  if (!atNonce.ok) throw new Error(`Safe Transaction Service responded ${atNonce.status}`)
  const { results } = (await atNonce.json()) as { results: ServiceSafeTxStatus[] }
  const isReplaced = results.some(
    tx => tx.isExecuted && lower(tx.safeTxHash) !== lower(proposal.safeTxHash)
  )
  return isReplaced ? { status: 'dropped' } : undefined
}
